/**
 * Performance Table Component for Supplier Performance
 *
 * The card, pagers and row loop. The two-level header lives in
 * PerformanceTableHeader, a supplier row in PerformanceTableRow, a supplier's
 * per-customer-site rows in SiteBreakdownRows and the CR-7264 group band in
 * GroupTotalRow.
 *
 * Customer review 11 Sep (item 3/4): the rows follow the old portal's
 * model — group total (when filtered to a supplier group) → the supplier as a
 * whole → the supplier at each customer site, opened from the row's chevron.
 */

import { Fragment, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Pagination } from '../../components/ui/pagination';
import { Loader2 } from 'lucide-react';
import { SupplierProfile, SupplierRatingThreshold } from '../../lib/api';
import { useQualityMeasureLabel } from '../../lib/qualityLabels';
import { selfRowOf, selfRowKey } from './selfPerformance';
import { withGroupTotals } from './groupAggregate';
import { GroupTotalRow } from './GroupTotalRow';
import { PerformanceTableHeader, frozenColumns } from './PerformanceTableHeader';
import { PerformanceTableRow, RowCallbacks } from './PerformanceTableRow';
import { SiteBreakdownRows } from './SiteBreakdownRows';

interface PerformanceTableProps extends RowCallbacks {
  suppliers: SupplierProfile[];
  ratingThresholds: SupplierRatingThreshold[];
  currency: string;
  loading?: boolean;
  /**
   * SI-16: supplier-side page — rows are the caller's own company once per
   * customer. Hides the Supplier column (it would repeat the same name), keys
   * rows and drill-downs by customer, and grades each row with that
   * customer's bands.
   */
  viewerIsSupplier?: boolean;
  /** Q8: the loaded rows span more than one supplier site (GROUP view) — show the Supplier Site column even when a picker narrows the visible rows to one. */
  showSupplierSiteColumn?: boolean;
  /**
   * CR-7264: the Supplier Group filter is set to a specific group, so the
   * table is already narrowed to that group and its total is what the user
   * asked to see. Left off in the unfiltered list, where totals for every
   * group at once would break up the ranking the page exists to show.
   */
  groupFilterActive?: boolean;
  /** Customer review 11 Sep (msg 3/4): the page's params for one site × one supplier. */
  buildSiteRowParams?: (siteId: number, supplierId: number) => Record<string, string | number | boolean>;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    pageSize: number;
    hasMultiplePages: boolean;
    goToPage: (page: number) => void;
  };
}

export function PerformanceTable({
  suppliers,
  ratingThresholds,
  currency,
  loading,
  viewerIsSupplier = false,
  showSupplierSiteColumn = false,
  groupFilterActive = false,
  buildSiteRowParams,
  pagination,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: PerformanceTableProps) {
  const qualityLabel = useQualityMeasureLabel();
  // Per-row grade bands: the page-level thresholds on the customer page, the
  // row's own customer's thresholds on the supplier page (SI-16).
  const thresholdsFor = (s: SupplierProfile) => selfRowOf(s)?.rating_thresholds ?? ratingThresholds;
  // Q8: at GROUP view a supplier's rows span sibling sites — show which.
  const showSupplierSite = viewerIsSupplier && showSupplierSiteColumn;
  const itemLabel = viewerIsSupplier ? 'customers' : 'suppliers';
  const { columnCount } = frozenColumns(viewerIsSupplier, showSupplierSite);

  // Which suppliers have their per-site breakdown open. A new result set
  // (re-filter, another page, a view switch) starts collapsed.
  // Customer review 11 Sep (stream I): keyed on WHICH suppliers are listed,
  // not on the array's identity — the page derives `suppliers` with a fresh
  // .filter() on every render, so opening a drill-down from a site row (page
  // state) re-ran this and collapsed the breakdown behind the modal.
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const resultKey = suppliers.map((s) => s.company).join(',');
  useEffect(() => {
    setExpanded(new Set());
  }, [resultKey]);
  const toggle = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // CR-7264: filtering to a supplier group puts that group's total above its
  // member rows — and only then. In the unfiltered list this page is a ranking
  // of suppliers, and a total banded in above every group would interrupt it.
  // Not on the supplier-side page either: there the rows are the viewer's own
  // company once per customer, so a "supplier group" total is meaningless.
  const showGroupTotals = !viewerIsSupplier && groupFilterActive;
  const rows = showGroupTotals
    ? withGroupTotals(suppliers)
    : suppliers.map((supplier) => ({ kind: 'supplier' as const, supplier }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{viewerIsSupplier ? 'Your Performance by Customer' : 'Supplier Performance Overview'} ({pagination.totalCount})</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : suppliers.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">{viewerIsSupplier ? 'No customers found' : 'No suppliers found'}</p>
        ) : (
          <>
            {/* Top Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pt-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel={itemLabel}
                />
              </div>
            )}

            <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
              <table className="w-full min-w-[2680px]">
                <PerformanceTableHeader
                  viewerIsSupplier={viewerIsSupplier}
                  showSupplierSite={showSupplierSite}
                  qualityLabel={qualityLabel}
                />
                <tbody>
                  {rows.map((entry) => {
                    if (entry.kind === 'group') {
                      return (
                        <GroupTotalRow
                          key={`group-${entry.group.groupId}`}
                          group={entry.group}
                          colSpan={columnCount}
                          qualityLabel={qualityLabel}
                          currency={currency}
                        />
                      );
                    }
                    const supplier = entry.supplier;
                    const sites = supplier.customer_sites ?? [];
                    const expandable = !viewerIsSupplier && !!buildSiteRowParams && sites.length > 1;
                    const isOpen = expandable && expanded.has(supplier.company);
                    return (
                      <Fragment key={selfRowKey(supplier)}>
                        <PerformanceTableRow
                          supplier={supplier}
                          thresholds={thresholdsFor(supplier)}
                          currency={currency}
                          viewerIsSupplier={viewerIsSupplier}
                          showSupplierSite={showSupplierSite}
                          expandable={expandable}
                          expanded={isOpen}
                          onToggleExpand={() => toggle(supplier.company)}
                          onOpenGRNModal={onOpenGRNModal}
                          onOpenNCRModal={onOpenNCRModal}
                          onOpenPOLineModal={onOpenPOLineModal}
                        />
                        {isOpen && buildSiteRowParams && (
                          <SiteBreakdownRows
                            supplier={supplier}
                            thresholds={thresholdsFor(supplier)}
                            currency={currency}
                            columnCount={columnCount}
                            buildSiteRowParams={buildSiteRowParams}
                            onOpenGRNModal={onOpenGRNModal}
                            onOpenNCRModal={onOpenNCRModal}
                            onOpenPOLineModal={onOpenPOLineModal}
                          />
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Bottom Pagination */}
            {pagination.hasMultiplePages && (
              <div className="px-6 pb-4">
                <Pagination
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalCount={pagination.totalCount}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={pagination.goToPage}
                  itemLabel={itemLabel}
                />
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
